import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { formatError } from '../utils/errorHandler';
import { PRICING_PLANS, DEFAULT_PLAN_ID } from '../config/pricingPlans';
import Modal from './Modal';
import './CreateCompanyModal.css';

const PLAN_I18N_KEYS = {
  basic: {
    name: 'planBasicName',
    description: 'planBasicDescription',
    features: [
      'planBasicFeatureUsers',
      'planBasicFeatureTaskManagement',
      'planBasicFeatureSupport'
    ]
  },
  professional: {
    name: 'planProfessionalName',
    description: 'planProfessionalDescription',
    badge: 'planProfessionalBadge',
    features: [
      'planProfessionalFeatureUsers',
      'planProfessionalFeatureTaskManagement',
      'planProfessionalFeaturePresets',
      'planProfessionalFeaturePhotoUploads',
      'planProfessionalFeatureExport',
      'planProfessionalFeatureSupport'
    ]
  },
  enterprise: {
    name: 'planEnterpriseName',
    description: 'planEnterpriseDescription',
    features: [
      'planEnterpriseFeatureUsers',
      'planEnterpriseFeatureAllFeatures',
      'planEnterpriseFeatureSupport',
      'planEnterpriseFeatureIntegrations',
      'planEnterpriseFeatureAnalytics'
    ]
  }
};

const CreateCompanyModal = ({ isOpen, onClose, onSuccess, defaultPricingPlan = DEFAULT_PLAN_ID }) => {
  const { t } = useTranslation('companies');
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    pricingPlan: defaultPricingPlan
  });

  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        pricingPlan: defaultPricingPlan
      }));
    }
  }, [defaultPricingPlan, isOpen]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const formatPlanPrice = (plan) => {
    if (plan.price === 0) {
      return t('priceFree');
    }
    return t('pricePerMonth', { price: plan.price });
  };

  const getPlanTranslation = (planId, field) => {
    const keys = PLAN_I18N_KEYS[planId];
    if (!keys || !keys[field]) {
      return null;
    }
    return t(keys[field]);
  };

  const getPlanFeatures = (planId) => {
    const keys = PLAN_I18N_KEYS[planId];
    if (!keys?.features) {
      return [];
    }
    return keys.features.map((key) => t(key));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post('/api/companies', {
        name: formData.name,
        address: formData.address || null,
        phone: formData.phone || null,
        email: formData.email || null,
        pricingPlan: formData.pricingPlan
      });

      if (response.data.success) {
        setFormData({
          name: '',
          address: '',
          phone: '',
          email: '',
          pricingPlan: defaultPricingPlan
        });
        if (onSuccess) {
          onSuccess(response.data.data);
        }
        onClose();
      }
    } catch (error) {
      setError(formatError(error) || t('createModalFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('createModalTitle')}
      className="create-company-modal"
    >
      <form onSubmit={handleSubmit} className="create-company-form">
        {error && <div className="error">{error}</div>}
          
          <div className="create-company-form-group">
            <label htmlFor="name">{t('createModalCompanyName')}</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder={t('createModalCompanyNamePlaceholder')}
            />
          </div>

          <div className="create-company-form-group">
            <label htmlFor="email">{t('formEmail')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('createModalEmailPlaceholder')}
            />
          </div>

          <div className="create-company-form-group">
            <label htmlFor="phone">{t('formPhone')}</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder={t('createModalPhonePlaceholder')}
            />
          </div>

          <div className="create-company-form-group">
            <label htmlFor="address">{t('formAddress')}</label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              rows="3"
              placeholder={t('createModalAddressPlaceholder')}
            />
          </div>

          <div className="create-company-form-group">
            <label htmlFor="pricingPlan">{t('createModalPricingPlan')}</label>
            <div className="pricing-plans-grid">
              {PRICING_PLANS.map((plan) => (
                <label
                  key={plan.id}
                  className={`pricing-plan-card ${formData.pricingPlan === plan.id ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="pricingPlan"
                    value={plan.id}
                    checked={formData.pricingPlan === plan.id}
                    onChange={handleChange}
                    required
                  />
                  <div className="pricing-plan-content">
                    <div className="pricing-plan-header">
                      <h3>{getPlanTranslation(plan.id, 'name') || plan.name}</h3>
                      <div className="pricing-plan-price">{formatPlanPrice(plan)}</div>
                    </div>
                    <p className="pricing-plan-description">
                      {getPlanTranslation(plan.id, 'description') || plan.description}
                    </p>
                    <ul className="pricing-plan-features">
                      {getPlanFeatures(plan.id).map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                </label>
              ))}
            </div>
            {formData.pricingPlan !== DEFAULT_PLAN_ID && (
              <p className="create-company-paid-plan-note">
                {t('createModalPaidPlanNote')}
              </p>
            )}
          </div>

          <div className="create-company-modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              {t('cancel', { ns: 'common' })}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('createModalCreating') : t('createModalSubmit')}
            </button>
          </div>
      </form>
    </Modal>
  );
};

export default CreateCompanyModal;
